import { useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import useSortableData from 'lib/hooks/use-sortable-data';
import { TableHead, TableRow } from './grid-table-elements';
import Loader from 'components/common/loader/loader';

import {
  StyledTableWrapper,
  StyledTable,
  StyledTableBody,
  StyledOverflow
} from './grid-table.style';

import { GridTableProps } from 'types';

const GridTable = <T,>({
  uniqueKey = 'grid-table',
  variant = 'default',
  cols = 5,
  accountId,
  loading = false,
  isLoading,
  hasMore,
  setPage = () => {},
  columns,
  componentEmpty = <>No items</>,
  hover = false,
  data = [],
  defaultSort = null,
}: GridTableProps<T>) => {
  const { items, requestSort, sortConfig } = useSortableData(data, defaultSort);
  const loaderRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const { ref } = useInView({
    root: tableRef.current,
    threshold: 0,
    onChange: (inView) => {
      if (inView && hasMore && !isLoading) {
        setPage((prevPage) => prevPage + 1);
      }
    },
  });

  return (
    <StyledTableWrapper>
      <StyledTable>
        <TableHead
          cols={cols}
          variant={variant}
          columns={columns}
          requestSort={requestSort}
          sortConfig={sortConfig}
        />
        <StyledTableBody className={`variant-${variant}`}>
          <StyledOverflow
            className={`variant-${variant}`}
            ref={tableRef}
          >
            {loading ? (
              <Loader />
            ) : items.length === 0 ? (
              componentEmpty
            ) : (
              items.map((item, i) => (
                <TableRow
                  key={uniqueKey + i}
                  variant={variant}
                  columns={columns}
                  item={item}
                  accountId={accountId}
                  hover={hover}
                  isLast={i === items.length - 1}
                  ref={ref}
                />
              ))
            )}
            {isLoading && hasMore && <Loader ref={loaderRef} />}
          </StyledOverflow>
        </StyledTableBody>
      </StyledTable>
    </StyledTableWrapper>
  );
};

export default GridTable;
